import React, { useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import { firestore } from "../../firebase";
import { collection, getDocs } from "firebase/firestore";

import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay, Pagination } from "swiper/modules";
import "swiper/css";
import "swiper/css/pagination";

const HeroWrap = styled.section`
  padding: 22px 18px 10px;
  background: radial-gradient(1200px 420px at 20% 0%, rgba(15,118,110,0.10), transparent 55%),
    radial-gradient(900px 360px at 90% 10%, rgba(195,122,71,0.14), transparent 55%),
    #f7fafc;
`;

const Inner = styled.div`
  max-width: 1200px;
  margin: 0 auto;
`;

const Card = styled.div`
  background: #fff;
  border: 1px solid #e5e7eb;
  box-shadow: 0 18px 55px rgba(0, 0, 0, 0.08);
  border-radius: 22px;
  overflow: hidden;
`;

/* -------- Avdelningar / tabs -------- */

const TabsBar = styled.div`
  padding: 14px 14px 0;
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
`;

const Tab = styled.button`
  appearance: none;
  border: 1px solid #e5e7eb;
  background: ${({ $active }) => ($active ? "rgba(15,118,110,0.12)" : "#fff")};
  color: ${({ $active }) => ($active ? "#0f766e" : "#111827")};
  font-weight: 950;
  padding: 9px 12px;
  border-radius: 999px;
  cursor: pointer;
  transition: transform 140ms ease, background 140ms ease;

  &:hover {
    background: ${({ $active }) => ($active ? "rgba(15,118,110,0.16)" : "#f9fafb")};
    transform: translateY(-1px);
  }

  &:focus-visible {
    outline: 2px solid rgba(15,118,110,0.45);
    outline-offset: 3px;
  }
`;

const TabsMeta = styled.div`
  padding: 8px 14px 0;
  color: #6b7280;
  font-weight: 900;
  font-size: 12px;
`;

/* -------- Carousel slide -------- */

const Slide = styled.div`
  position: relative;
  height: 420px;

  @media (max-width: 900px) {
    height: 420px;
  }
  @media (max-width: 520px) {
    height: 360px;
  }
`;

const Img = styled.img`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const Overlay = styled.div`
  position: absolute;
  inset: 0;
  background: linear-gradient(
    180deg,
    rgba(0,0,0,0.25) 0%,
    rgba(0,0,0,0.65) 78%,
    rgba(0,0,0,0.75) 100%
  );
`;

const Caption = styled.div`
  position: absolute;
  left: 16px;
  right: 16px;
  bottom: 16px;

  background: rgba(255, 255, 255, 0.92);
  border: 1px solid rgba(229, 231, 235, 0.9);
  border-radius: 18px;
  box-shadow: 0 16px 44px rgba(0, 0, 0, 0.18);
  padding: 14px 14px;

  @media (min-width: 1024px) {
    max-width: 560px;
  }
`;

const Title = styled.h1`
  margin: 0;
  font-weight: 950;
  color: #111827;
  font-size: clamp(22px, 3vw, 40px);
  line-height: 1.12;
`;

const Sub = styled.p`
  margin: 8px 0 0;
  color: #4b5563;
  line-height: 1.55;
  font-size: 14px;
`;

const Hint = styled.div`
  position: absolute;
  top: 14px;
  left: 14px;
  padding: 8px 12px;
  border-radius: 999px;
  background: rgba(0, 0, 0, 0.55);
  color: #fff;
  font-weight: 900;
  font-size: 12px;
  letter-spacing: 0.2px;
  display: none;

  @media (max-width: 768px) {
    display: inline-flex;
  }
`;

const Skeleton = styled.div`
  height: 420px;
  background: linear-gradient(90deg, #f3f4f6, #fafafa, #f3f4f6);
  background-size: 200% 100%;
  animation: shimmer 1.1s infinite linear;

  @keyframes shimmer {
    0% { background-position: 0% 0; }
    100% { background-position: 200% 0; }
  }

  @media (max-width: 520px) {
    height: 360px;
  }
`;

const GalleryHeroCarousel = ({
  title = "Hofors BGK",
  subtitle = "Bangolf & äventyrsgolf i Hofors – välkommen!",
  limit = 10,
  autoplayMs = 5000,

  // Om du vill låsa till en viss folder från parent kan du fortfarande göra det:
  folder = "",

  // Slumpa bilderna:
  randomize = true,

  // ✅ NYTT: visa avdelningar/tabs
  showFolders = true,

  // ✅ NYTT: börja på en viss avdelning ("Alla" annars)
  defaultFolder = "Alla",
}) => {
  const [loading, setLoading] = useState(true);
  const [allImages, setAllImages] = useState([]);

  const [folders, setFolders] = useState(["Alla"]);
  const [activeFolder, setActiveFolder] = useState(defaultFolder || "Alla");

  useEffect(() => {
    const fetchGalleryImages = async () => {
      try {
        setLoading(true);
        const snap = await getDocs(collection(firestore, "gallery"));
        const all = snap.docs.map((d) => ({ id: d.id, ...d.data() }));

        // bilder
        const images = all
          .filter((x) => x.type === "image" && x.url)
          .map((x) => ({
            id: x.id,
            url: x.url,
            folder: (x.folder || "").trim(),
          }));

        // folders (avdelningar)
        const uniqueFolders = Array.from(
          new Set(images.map((x) => x.folder).filter(Boolean))
        ).sort((a, b) => a.localeCompare(b, "sv"));

        setFolders(["Alla", ...uniqueFolders]);
        setAllImages(images);
      } finally {
        setLoading(false);
      }
    };

    fetchGalleryImages();
  }, []);

  // Om parent skickar in folder prop (låst) så vinner den
  useEffect(() => {
    if (folder && folder.trim()) {
      setActiveFolder(folder.trim());
    }
  }, [folder]);

  const items = useMemo(() => {
    let images = [...allImages];

    // 1) låst folder (prop) eller aktiv tab
    const chosen = (folder && folder.trim()) ? folder.trim() : activeFolder;

    if (chosen && chosen !== "Alla") {
      images = images.filter((x) => x.folder === chosen);
    }

    // 2) slumpa
    if (randomize) {
      images = images.sort(() => Math.random() - 0.5);
    }

    // 3) limit
    return images.slice(0, limit);
  }, [allImages, activeFolder, folder, randomize, limit]);

  const hasSlides = items.length > 0;
  const chosenFolder = (folder && folder.trim()) ? folder.trim() : activeFolder;

  return (
    <HeroWrap aria-label="Bildkarusell från galleriet">
      <Inner>
        <Card>
          {/* ✅ Avdelningar */}
          {showFolders && !folder && (
            <>
              <TabsBar>
                {folders.map((f) => (
                  <Tab
                    key={f}
                    type="button"
                    $active={activeFolder === f}
                    onClick={() => setActiveFolder(f)}
                  >
                    {f}
                  </Tab>
                ))}
              </TabsBar>
              <TabsMeta>
                Visar: <strong>{chosenFolder || "Alla"}</strong>
              </TabsMeta>
            </>
          )}

          {loading ? (
            <Skeleton />
          ) : !hasSlides ? (
            <Slide>
              <Overlay />
              <Caption>
                <Title>{title}</Title>
                <Sub>
                  {subtitle}
                  {chosenFolder && chosenFolder !== "Alla" ? ` • ${chosenFolder}` : ""}
                </Sub>
              </Caption>
            </Slide>
          ) : (
            <Swiper
              modules={[Autoplay, Pagination]}
              slidesPerView={1}
              loop={items.length > 1}
              autoplay={{
                delay: autoplayMs,
                disableOnInteraction: false,
              }}
              pagination={{ clickable: true }}
            >
              {items.map((it, idx) => (
                <SwiperSlide key={`${it.id}-${idx}`}>
                  <Slide>
                    <Img src={it.url} alt={`Galleri-bild ${idx + 1}`} loading="lazy" />
                    <Overlay />
                    <Hint>Swipa</Hint>

                    <Caption>
                      <Title>{title}</Title>
                      <Sub>
                        {subtitle}
                        {it.folder ? ` • ${it.folder}` : ""}
                      </Sub>
                    </Caption>
                  </Slide>
                </SwiperSlide>
              ))}
            </Swiper>
          )}
        </Card>
      </Inner>
    </HeroWrap>
  );
};

export default GalleryHeroCarousel;

